import React, { useEffect, useRef, useState, useMemo } from "react";
import { loadModules, setDefaultOptions } from "esri-loader";
import { useNavigate } from "react-router-dom";
import TrailDetailPanel from "./TrailDetailPanel";
import { useUserLocation } from "@/hooks/useUserLocation";

interface TrailMap2Props {
  className?: string;
  mapType?: string;
  segments?: any[];
  routes?: any[];
  runs?: any[];
  singleTrailId?: string; // For trail detail page - show only this trail
  disableScrollZoom?: boolean; // Disable mouse wheel zoom
  initialCenter?: [number, number]; // Optional initial center override
  initialZoom?: number; // Optional initial zoom override
}

export default function TrailMap2({
  className,
  segments,
  singleTrailId,
  disableScrollZoom = false,
  initialCenter,
  initialZoom,
}: TrailMap2Props) {
  const navigate = useNavigate();
  const mapDiv = useRef<HTMLDivElement>(null);
  const trailLayerRef = useRef<any>(null);
  const isDestroyedRef = useRef(false);
  const [selectedTrailId, setSelectedTrailId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [hoverInfo, setHoverInfo] = useState<{
    trailName: string;
    shortName?: string;
    difficulty?: string;
    recentRunCount?: number;
    x: number;
    y: number;
  } | null>(null);

  const { location: userLocation } = useUserLocation();

  // Map difficulty level to symbol
  const getDifficultySymbol = (difficulty: string | undefined): string => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "○";
      case "moderate":
        return "◻";
      case "hard":
        return "◇";
      case "extreme":
        return "◇◇";
      default:
        return "";
    }
  };

  // Memoize map center to prevent re-renders from causing map reinitialization
  // Priority: 1) initialCenter prop, 2) user's home location, 3) default (SoCal)
  const mapCenter = useMemo(
    () =>
      initialCenter ||
      (userLocation?.home_longitude && userLocation?.home_latitude
        ? ([userLocation.home_longitude, userLocation.home_latitude] as [number, number])
        : ([-117.1893, 34.2515] as [number, number])),
    [initialCenter, userLocation?.home_longitude, userLocation?.home_latitude],
  );

  // Memoize zoom to prevent unnecessary re-renders
  const mapZoom = useMemo(() => initialZoom ?? 10, [initialZoom]);

  useEffect(() => {
    if (!mapDiv.current) return;

    // Reset destroyed flag on mount
    isDestroyedRef.current = false;

    // Use AMD loader from the ArcGIS CDN to avoid bundling @arcgis/core (prevents build OOM)
    setDefaultOptions({ version: "4.33" });

    let view: any = null;
    let trailLayer: any = null;

    loadModules(
      [
        "esri/Map",
        "esri/views/MapView",
        "esri/layers/VectorTileLayer",
        "esri/layers/GeoJSONLayer",
        "esri/layers/support/LabelClass",
        "esri/symbols/TextSymbol",
        "esri/layers/support/FeatureEffect",
        "esri/widgets/Zoom",
        "esri/widgets/BasemapToggle",
        "esri/Basemap",
      ],
      { css: true },
    )
      .then(
        ([
          EsriMap,
          MapView,
          VectorTileLayer,
          GeoJSONLayer,
          LabelClass,
          TextSymbol,
          FeatureEffect,
          Zoom,
          BasemapToggle,
          Basemap,
        ]) => {
          if (isDestroyedRef.current) return; // Early exit if already unmounted

          // Outdoors vector tile layer via portal item
          const outdoorsLayer = new VectorTileLayer({
            portalItem: { id: "ef4bc627ccab4a80990ed476d53020d6" }, // Alt: f320a8af9ffa4b4baeaf1d66f03da86f
          });

          // BASEMAP TOGGLE FIX: Wrap outdoors layer in Basemap object
          // To revert: Remove outdoorsBasemap, put outdoorsLayer back in layers array, remove basemap property
          const outdoorsBasemap = new Basemap({
            baseLayers: [outdoorsLayer]
          });

          const map = new EsriMap({
            basemap: outdoorsBasemap, // BASEMAP TOGGLE FIX: Set as basemap instead of layer
            // REVERT: layers: [outdoorsLayer],
          });

          view = new MapView({
            container: mapDiv.current as HTMLDivElement,
            map,
            center: mapCenter,
            zoom: mapZoom,
            navigation: {
              mouseWheelZoomEnabled: !disableScrollZoom,
            },
            ui: {
              components: [], // Remove all default UI components
            },
          });

          // Add zoom control to bottom-right
          const zoomWidget = new Zoom({
            view: view,
          });
          view.ui.add(zoomWidget, "bottom-right");

          // Add basemap toggle above zoom controls
          const satelliteBasemap = new Basemap({
            portalItem: {
              id: "d802f08316e84c6592ef681c50178f17", // World Imagery basemap (same as RunHeroMap)
            },
          });

          const basemapToggle = new BasemapToggle({
            view: view,
            nextBasemap: satelliteBasemap,
          });
          view.ui.add(basemapToggle, {
            position: "bottom-right",
            index: 0,
          });

          view.when(() => {
            if (isDestroyedRef.current) return; // Check again after async operation

            // Add recent runs layer (underneath trail segments)
            const recentRunsLayer = new GeoJSONLayer({
              url: "https://ogripwmsopctiljhqivm.supabase.co/functions/v1/recent-runs-geojson?days=365",
              title: "Recent Activity",
              effect: "bloom(2, 1px, 5%)",
              renderer: {
                type: "simple",
                symbol: {
                  type: "simple-line",
                  color: [255, 198, 102, 0.1], // Brand amber at 70% opacity
                  width: 4,
                  cap: "round",
                  join: "round",
                },
              } as any,
            });
            map.add(recentRunsLayer);

            // Use edge function URL directly to get recent_run_count data
            // If segments prop is provided, we still use the edge function for consistency and to get run counts
            trailLayer = new GeoJSONLayer({
              url: "https://ogripwmsopctiljhqivm.supabase.co/functions/v1/trail-segments-geojson",
              title: "Trail Segments",
              outFields: ["*"],
              renderer: {
                type: "simple",
                symbol: {
                  type: "cim",
                  data: {
                    type: "CIMSymbolReference",
                    symbol: {
                      type: "CIMLineSymbol",
                      symbolLayers: [
                        {
                          type: "CIMSolidStroke",
                          effects: [
                            {
                              type: "CIMGeometricEffectOffset",
                              method: "Bevelled",
                              offset: 0,
                              option: "Fast",
                            },
                            {
                              type: "CIMGeometricEffectDashes",
                              lineDashEnding: "NoConstraint",
                              controlPointEnding: "NoConstraint",
                            },
                          ],
                          enable: true,
                          colorLocked: true,
                          capStyle: "Round",
                          joinStyle: "Round",
                          lineStyle3D: "Strip",
                          miterLimit: 10,
                          width: 1,
                          color: [225, 137, 55, 255],
                          animations: [],
                        },
                        {
                          type: "CIMSolidStroke",
                          effects: [
                            {
                              type: "CIMGeometricEffectOffset",
                              method: "Mitered",
                              offset: 0,
                              option: "Fast",
                            },
                            {
                              type: "CIMGeometricEffectDashes",
                              lineDashEnding: "NoConstraint",
                              controlPointEnding: "NoConstraint",
                            },
                          ],
                          enable: true,
                          colorLocked: true,
                          capStyle: "Round",
                          joinStyle: "Round",
                          lineStyle3D: "Strip",
                          miterLimit: 10,
                          width: 2.5,
                          color: [112, 63, 26, 255],
                          animations: [],
                        },
                        {
                          type: "CIMSolidStroke",
                          effects: [
                            {
                              type: "CIMGeometricEffectOffset",
                              method: "Mitered",
                              offset: 0,
                              option: "Fast",
                            },
                            {
                              type: "CIMGeometricEffectDashes",
                              lineDashEnding: "NoConstraint",
                              controlPointEnding: "NoConstraint",
                            },
                          ],
                          enable: true,
                          colorLocked: true,
                          capStyle: "Round",
                          joinStyle: "Round",
                          lineStyle3D: "Strip",
                          miterLimit: 10,
                          width: 5,
                          color: [255, 255, 255, 125],
                          animations: [],
                        },
                      ],
                      animations: [],
                    },
                  },
                },
                visualVariables: [
                  {
                    type: "size",
                    valueExpression: "$view.scale",
                    stops: [
                      { size: 5.94 * 1.2, value: 2256.99 },
                      { size: 3.06 * 1.2, value: 18055.95 },
                      { size: 2.4 * 1.2, value: 144447.63 },
                      { size: 1.32 * 1.2, value: 1155581.1 },
                    ],
                  },
                ],
              } as any,
            });

              // Add hover handler for trail name tooltip
              view.on("pointer-move", (event: any) => {
                // Use ref to check destroyed state
                if (isDestroyedRef.current) {
                  return;
                }

                if (!view || view.destroyed) {
                  return;
                }

                if (!view.ready) {
                  return;
                }

                // Check trailLayer before using
                if (!trailLayer || trailLayer.destroyed) {
                  return;
                }

                try {
                  const hitTestPromise = view.hitTest(event);

                  if (!hitTestPromise) {
                    return;
                  }

                  hitTestPromise
                    .then((response: any) => {
                      // Check destroyed state after async
                      if (isDestroyedRef.current || view?.destroyed || trailLayer?.destroyed) {
                        return;
                      }

                      try {
                        if (response?.results?.length > 0) {
                          const graphic = response.results[0]?.graphic;

                          // Safety check for trailLayer being destroyed
                          if (!trailLayer || trailLayer.destroyed) {
                            return;
                          }

                          if (graphic?.layer === trailLayer) {
                            const attrs = graphic.attributes;
                            const segmentData = segments?.find((s) => s.id === attrs?.id);

                            // Prefer trail_name from attributes if segments prop not available
                            const trailName =
                              segmentData?.trail_name || segmentData?.name || attrs?.trail_name || attrs?.name;
                            const trailShortName = segmentData?.trail_name_short || attrs?.trail_name_short;
                            const difficulty = segmentData?.difficulty || attrs?.difficulty;
                            const recentRunCount = segmentData?.recent_run_count || attrs?.recent_run_count || 0;

                            if (trailName && !isDestroyedRef.current) {
                              setHoverInfo({
                                trailName,
                                shortName: trailShortName,
                                difficulty,
                                recentRunCount,
                                x: event.x,
                                y: event.y,
                              });
                            }
                          } else {
                            if (!isDestroyedRef.current) setHoverInfo(null);
                          }
                        } else {
                          if (!isDestroyedRef.current) setHoverInfo(null);
                        }
                      } catch {
                        // Silently ignore errors during hitTest processing
                      }
                    })
                    .catch(() => {
                      // Silently ignore hitTest errors (including AbortError during cleanup)
                    });
                } catch {
                  // Silently ignore synchronous errors
                }
              });

              // Add click handler for trail panel
              view.on("click", (event: any) => {
                if (isDestroyedRef.current || !view || view.destroyed) return;
                if (!trailLayer || trailLayer.destroyed) return;

                try {
                  view
                    .hitTest(event)
                    .then((response: any) => {
                      if (isDestroyedRef.current) return;

                      if (response?.results?.length > 0) {
                        const graphic = response.results[0]?.graphic;
                        if (graphic?.layer === trailLayer) {
                          const attrs = graphic.attributes;

                          // Find the original segment data to get trail_id
                          const segmentData = segments?.find((s) => s.id === attrs?.id);
                          const trailId = segmentData?.trail_id || attrs?.trail_id;

                          if (trailId) {
                            setSelectedTrailId(trailId);
                            setPanelOpen(true);
                            setHoverInfo(null); // Clear hover when clicking
                          }
                        } else {
                          setPanelOpen(false);
                          setSelectedTrailId(null);
                        }
                      } else {
                        setPanelOpen(false);
                        setSelectedTrailId(null);
                      }
                    })
                    .catch(() => {
                      // Silently ignore hitTest errors
                    });
                } catch {
                  // Silently ignore any errors
                }
              });

              map.add(trailLayer);

              // Store reference for feature effect updates
              trailLayerRef.current = trailLayer;

              // Apply feature effect for single trail highlighting
              if (singleTrailId) {
                trailLayer.featureEffect = new FeatureEffect({
                  filter: {
                    where: `trail_id = '${singleTrailId}'`,
                  },
                  includedEffect: "drop-shadow(1.5px, 1.5px, 2.25px, rgb(50,50,50))",
                  excludedEffect: "grayscale(50%)",
                });

                // Fit bounds to the highlighted trail
                view.when(() => {
                  if (isDestroyedRef.current) return;
                  trailLayer
                    .queryExtent({
                      where: `trail_id = '${singleTrailId}'`,
                    })
                    .then((result: any) => {
                      if (isDestroyedRef.current) return;
                      if (result.extent) {
                        view.goTo(result.extent.expand(1.5));
                      }
                    })
                    .catch(() => {
                      // Ignore errors if view destroyed
                    });
                });
              }
          });
        },
      )
      .catch((err) => {
        if (!isDestroyedRef.current) {
          console.error("Failed to load ArcGIS modules", err);
        }
      });

    return () => {
      isDestroyedRef.current = true;
      setHoverInfo(null);
      if (view) {
        view.container = null;
        view.destroy();
        view = null;
      }
      trailLayerRef.current = null;
    };
  }, [segments, singleTrailId, navigate, disableScrollZoom, mapCenter, mapZoom]);

  // Apply feature effect when trail is selected (only for non-detail pages)
  useEffect(() => {
    if (singleTrailId || !trailLayerRef.current) return; // Skip if we're on trail detail page or layer not ready

    if (selectedTrailId && panelOpen) {
      loadModules(["esri/layers/support/FeatureEffect"])
        .then(([FeatureEffect]) => {
          if (trailLayerRef.current) {
            trailLayerRef.current.featureEffect = new FeatureEffect({
              filter: {
                where: `trail_id = '${selectedTrailId}'`,
              },
              includedEffect: "drop-shadow(1.5px, 1.5px, 2.25px, rgb(50,50,50))",
              excludedEffect: "grayscale(50%)",
            });
          }
        })
        .catch((err) => {
          console.error("Failed to apply feature effect", err);
        });
    } else {
      if (trailLayerRef.current) {
        trailLayerRef.current.featureEffect = null;
      }
    }
  }, [selectedTrailId, panelOpen, singleTrailId]);

  return (
    <>
      <div className={`${className} relative`} style={{ width: "100%", height: "100%" }}>
        <div ref={mapDiv} className="absolute inset-0" />
        {hoverInfo && (
          <div
            className="absolute z-50 px-3 py-2 bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg pointer-events-none"
            style={{ left: `${hoverInfo.x + 12}px`, top: `${hoverInfo.y + 12}px` }}
          >
            <div className="flex items-center gap-2">
              {hoverInfo.difficulty && (
                <span className="text-base font-semibold">{getDifficultySymbol(hoverInfo.difficulty)}</span>
              )}
              <span className="text-sm font-medium">{hoverInfo.trailName}</span>
              {hoverInfo.shortName && (
                <span className="px-2 py-0.5 text-xs bg-muted/60 text-muted-foreground rounded-full">
                  {hoverInfo.shortName}
                </span>
              )}
            </div>
            {hoverInfo.recentRunCount != null && hoverInfo.recentRunCount > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                Just ran {hoverInfo.recentRunCount} time{hoverInfo.recentRunCount > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
        <TrailDetailPanel
          trailId={selectedTrailId}
          open={panelOpen}
          onClose={() => {
            setPanelOpen(false);
            setSelectedTrailId(null);
          }}
        />
      </div>
    </>
  );
}
